import {Component, inject} from '@angular/core';
import {CommonModule} from '@angular/common';
import {Router, RouterLink} from '@angular/router';
import {DxDataGridModule, DxFormModule, DxTemplateModule} from 'devextreme-angular';
import {DxDataGridTypes} from 'devextreme-angular/ui/data-grid';
import {GridPageComponent, FilterConfig} from "../../../common/grid-page.component";
import {LoadGridViewDto, MissedOpportunity, OrderAppointmentStatus, ProductionStatus} from "@services";
import {FilterPanelComponent} from "../../../../components/library/filter-panel/filter-panel.component";
import EnumUtils from "../../../../shared/enum-utils";
import {ProductionStatusPipe} from "../../../../pipes/production-status.pipe";
import {OrderAppointmentStatusPipe} from "../../../../pipes/order-appointment-status.pipe";
import {MissedOpportunityPipe} from "../../../../pipes/missed-opportunity.pipe";
import {EasternTimePipe} from "../../../../pipes/eastern-time.pipe";

@Component({
    selector: 'app-load-level',
    standalone: true,
    imports: [
        CommonModule,
        DxDataGridModule,
        DxFormModule,
        DxTemplateModule,
        FilterPanelComponent,
        RouterLink,
        ProductionStatusPipe,
        OrderAppointmentStatusPipe,
        MissedOpportunityPipe,
        EasternTimePipe
    ],
    providers: [ProductionStatusPipe, OrderAppointmentStatusPipe, MissedOpportunityPipe, EasternTimePipe],
    templateUrl: './load-level.component.html',
    styles: [
        `.grid {
            height: calc(100vh - 140px);
        }`
    ]
})
export class LoadLevelComponent extends GridPageComponent<LoadGridViewDto> {
    private router = inject(Router);
    private productionStatusPipe = inject(ProductionStatusPipe);
    private orderAppointmentStatusPipe = inject(OrderAppointmentStatusPipe);
    private missedOpportunityPipe = inject(MissedOpportunityPipe);
    private easternTimePipe = inject(EasternTimePipe);

    dataSource = this.dataSources.loads();

    missedOpportunityHeaderFilter = {
        dataSource: EnumUtils.getEnumOptions(MissedOpportunity).map(({value}) => ({
            text: this.missedOpportunityPipe.transform(value),
            value: [this.gridFieldName('missedOpportunities'), 'contains', value],
        })),
    };

    productionStatusHeaderFilter = {
        dataSource: EnumUtils.getEnumOptions(ProductionStatus).map(({value}) => ({
            text: this.productionStatusPipe.transform(value),
            value: [this.gridFieldName('productionStatuses'), 'contains', value],
        })),
    };

    appointmentStatusHeaderFilter = {
        dataSource: EnumUtils.getEnumOptions(OrderAppointmentStatus).map(({value}) => ({
            text: this.orderAppointmentStatusPipe.transform(value),
            value: [this.gridFieldName('appointmentStatuses'), 'contains', value],
        })),
    };

    exceptionHeaderFilter = {
        dataSource: [
            {text: 'Data Conflict', value: [this.gridFieldName('hasDataConflict'), '=', true]},
            {text: '(Blanks)', value: [this.gridFieldName('hasDataConflict'), '=', false]},
        ],
    };

    protected override exportName: string = "PO Load Level";

    protected override filterConfig: FilterConfig[] = [
        {field: 'logisticalGroup', op: 'contains'},
        {field: 'overallWmStatus', op: 'contains'},
        {field: 'stagingArea', op: 'contains'},
        {field: 'shipToId', op: 'contains'},
        {field: 'shipToName', op: 'contains'}
    ];

    constructor() {
        super();
        this.init(LoadGridViewDto);
    }

    override async onRowDblClick(e: DxDataGridTypes.RowDblClickEvent) {
        const {data} = e;
        await this.router.navigate([`/outbound/orders/load/details/${data.logisticalGroup}`]);
    }

    protected getProductionBadgeClass(status: ProductionStatus): string {
        return EnumUtils.getProductionStatusBadgeClass(status);
    }

    protected getAppointmentStatusBadgeClass(status: OrderAppointmentStatus): string {
        return EnumUtils.getOrderAppointmentStatusBadgeClass(status);
    }

    calculateProductionStatusesCellValue = (load: LoadGridViewDto): string =>
        (load.productionStatuses ?? []).map(status => this.productionStatusPipe.transform(status)).join(', ');

    calculateAppointmentStatusesCellValue = (load: LoadGridViewDto): string =>
        (load.appointmentStatuses ?? []).map(status => this.orderAppointmentStatusPipe.transform(status)).join(', ');

    calculateMissedOpportunityCellValue = (load: LoadGridViewDto): string =>
        (load.missedOpportunities ?? []).map(opportunity => this.missedOpportunityPipe.transform(opportunity)).join(', ');

    calculateMdtsCellValue = (load: LoadGridViewDto): string =>
        (load.mdts ?? []).map(mdt => `${this.easternTimePipe.transform(mdt)} ET`).join(', ');

    calculateAppointmentsCellValue = (load: LoadGridViewDto): string =>
        (load.appointments ?? []).map(appointment => `${this.easternTimePipe.transform(appointment)} ET`).join(', ');

    calculateExceptionCellValue = (load: LoadGridViewDto): string =>
        load.hasDataConflict ? 'Data Conflict' : '';
}
